import { useQuery, type QueryClient } from '@tanstack/react-query';
import type { NavItem } from '@/ds';
import { automationQuery } from '@/features/automation/api';
import { flaggedDecidedQuery, pendingCandidatesQuery } from '@/features/candidates/api';
import { openGroupsQuery } from '@/features/duplicates/api';
import { useModelOptions } from '@/features/model/api';
import { organizationCountsQuery } from '@/features/organizations/api';
import {
  RECENT_MS,
  pollWhileIdle,
  recentRunsQuery,
  spendQuery,
  useRefreshWhenRunsFinish,
  useSpend,
} from '@/features/runs/api';
import { sourcesQuery } from '@/features/sources/api';
import { IDLE_MS } from '@/features/runs/vocab';
import { assembleInbox } from './assemble';

// The duplicate groups list's first page: the same cache entry, so the two agree.
const FIRST_GROUPS = { page: 1 };

/** What the Inbox reads, started early by its route. */
export function prefetchInbox(queryClient: QueryClient) {
  void queryClient.prefetchQuery(pendingCandidatesQuery());
  void queryClient.prefetchQuery(flaggedDecidedQuery());
  void queryClient.prefetchQuery(openGroupsQuery(FIRST_GROUPS));
  void queryClient.prefetchQuery(sourcesQuery());
  void queryClient.prefetchQuery(recentRunsQuery());
  void queryClient.prefetchQuery(spendQuery());
  void queryClient.prefetchQuery(automationQuery());
}

/**
 * The Inbox, assembled in the browser from the queries the pages use: pending candidates, decided
 * records flagged possibly cancelled, the first page of open duplicate groups, sources with the API's health verdicts, the latest runs
 * (the week's count, the last run, and polling while one is going), the spend summary, and
 * automation with its schedule (the all-clear names the next run). The Inbox page passes `poll` to
 * read sources and automation once a minute, live run or not, and recent runs at their own rate
 * while one is live; the nav counts don't.
 */
export function useInbox({ poll = false }: { poll?: boolean } = {}) {
  const pending = useQuery(pendingCandidatesQuery());
  const flagged = useQuery(flaggedDecidedQuery());
  const groups = useQuery(openGroupsQuery(FIRST_GROUPS));
  const recent = useQuery(
    poll ? { ...recentRunsQuery(), refetchInterval: pollWhileIdle(RECENT_MS) } : recentRunsQuery(),
  );
  const sources = useQuery(poll ? { ...sourcesQuery(), refetchInterval: IDLE_MS } : sourcesQuery());
  const spend = useSpend();
  const models = useModelOptions();
  const automation = useQuery(
    poll ? { ...automationQuery(), refetchInterval: IDLE_MS } : automationQuery(),
  );

  const inbox = assembleInbox({
    pending: pending.data?.items,
    flagged: flagged.data?.items,
    groups: groups.data,
    sources: sources.data,
    spend: spend.data,
    runs: recent.data?.items,
    modelChosenAt: models.data?.chosenAt,
  });

  const parts = [
    { name: 'candidates', query: pending },
    { name: 'possibly cancelled events', query: flagged },
    { name: 'duplicate groups', query: groups },
    { name: 'sources', query: sources },
    { name: 'recent runs', query: recent },
    { name: 'AI spend', query: spend },
  ];
  // A failed refresh keeps the last answer; only a part with nothing to show counts as failed.
  const failed = parts.filter((p) => p.query.isError && p.query.data === undefined);

  return {
    ...inbox,
    loading: parts.some((p) => p.query.isLoading),
    failed,
    allFailed: failed.length === parts.length,
    pending: pending.data,
    sources: sources.data,
    groups: groups.data,
    recentRuns: recent.data?.items,
    spend: spend.data,
    automation: automation.data,
    retry: () => {
      for (const part of failed) void part.query.refetch();
    },
  };
}

type NavCount = Pick<NavItem, 'count' | 'countTone'>;

/** Nav counts: open exceptions on Inbox, open groups on Duplicate groups, pending approvals on
 * Organizations (the Organizations page's own counts). */
export function useNavCounts(): Partial<Record<string, NavCount>> {
  const inbox = useInbox();
  const organizations = useQuery(organizationCountsQuery());
  useRefreshWhenRunsFinish(inbox.recentRuns);
  return {
    inbox: inbox.loading ? undefined : { count: inbox.count, countTone: inbox.tone },
    duplicates: inbox.groups ? { count: inbox.groups.totalCount } : undefined,
    organizations: organizations.data ? { count: organizations.data.pending } : undefined,
  };
}
